import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SignalIcon } from "@/components/icons/SignalIcon";
import { cn } from "@/lib/utils";
import {
  ChatSession,
  useChatSession,
  useChatSessions,
  useDeleteSession,
} from "@/queries/chat";
import { formatDistanceToNow } from "date-fns";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ChatSidebarProps {
  currentSessionId: number | null;
  onSelectSession: (sessionId: number | null) => void;
  onNewChat: () => void;
}

// Max title length before truncation in sidebar
const MAX_TITLE_LENGTH = 25;

export function ChatSidebar({
  currentSessionId,
  onSelectSession,
  onNewChat,
}: ChatSidebarProps) {
  const navigate = useNavigate();
  const { data: sessions, isLoading } = useChatSessions();
  const deleteSession = useDeleteSession();
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const handleNewChat = () => {
    onNewChat();
    navigate("/chat");
  };

  const handleDeleteClick = (e: React.MouseEvent, sessionId: number) => {
    e.stopPropagation();
    setPendingDeleteId(sessionId);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteId === null) return;
    deleteSession.mutate(pendingDeleteId, {
      onSuccess: () => {
        if (currentSessionId === pendingDeleteId) {
          onSelectSession(null);
        }
        setPendingDeleteId(null);
      },
      onError: () => setPendingDeleteId(null),
    });
  };

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <TooltipProvider delayDuration={250}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/40 p-4">
          <h2 className="font-semibold tracking-tight">Conversations</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewChat}
            className="h-8 gap-1.5 rounded-full px-3 text-xs font-medium shadow-sm transition-all hover:bg-primary hover:text-primary-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            New Chat
          </Button>
        </div>

        {/* Sessions list */}
        <ScrollArea className="flex-1">
          <div className="p-3 pr-4">
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-16 animate-pulse rounded-xl bg-muted/50"
                  />
                ))}
              </div>
            ) : sessions?.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-sm text-muted-foreground">
                <SignalIcon className="mb-3 h-10 w-10 rounded-2xl opacity-50" />
                <p>No conversations yet.</p>
                <p className="mt-1 text-xs">Start a new chat to begin!</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {sessions?.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    isActive={currentSessionId === session.id}
                    onSelect={() => onSelectSession(session.id)}
                    onDelete={(e) => handleDeleteClick(e, session.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </TooltipProvider>

      {/* In-app confirmation dialog — replaces browser confirm() */}
      <Dialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete conversation?</DialogTitle>
            <DialogDescription>
              This conversation and all its messages will be permanently
              deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setPendingDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteSession.isPending}
            >
              {deleteSession.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SessionItemProps {
  session: ChatSession;
  isActive: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

function SessionItem({
  session,
  isActive,
  onSelect,
  onDelete,
}: SessionItemProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const sessionTitle = session.title || `Chat ${session.id}`;

  // Truncate title if too long, but show full title in tooltip on hover.
  // Also load full title if truncated to get first user message for better tooltip content.
  const isTruncated =
    sessionTitle.length > MAX_TITLE_LENGTH || sessionTitle.endsWith("...");
  const displayTitle =
    sessionTitle.length > MAX_TITLE_LENGTH
      ? sessionTitle.substring(0, MAX_TITLE_LENGTH).trimEnd() + "..."
      : sessionTitle;

  const shouldLoadFullTitle = isTooltipOpen && isTruncated;
  const { data: fullSession } = useChatSession(
    shouldLoadFullTitle ? session.id : null
  );
  const firstUserMessage = fullSession?.messages
    ?.find((m) => m.role === "user")
    ?.content?.trim();
  const tooltipTitle = firstUserMessage || sessionTitle;

  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-xl p-3 cursor-pointer transition-all duration-200 border",
        isActive
          ? "bg-primary/5 border-primary/20 text-primary shadow-sm"
          : "bg-transparent border-transparent hover:bg-muted/50 hover:border-border/50"
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <div
        className={cn(
          "mt-0.5 h-9 w-9 overflow-hidden rounded-full transition-colors",
          isActive
            ? "ring-1 ring-primary/20"
            : "group-hover:ring-1 group-hover:ring-border/50"
        )}
      >
        <SignalIcon className="h-full w-full" />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden pr-1">
        <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "block w-full whitespace-nowrap overflow-hidden text-ellipsis text-sm font-medium transition-colors",
                isActive ? "text-primary" : "text-foreground"
              )}
            >
              {displayTitle}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            className="max-w-md whitespace-normal break-words"
          >
            {tooltipTitle}
          </TooltipContent>
        </Tooltip>
        <div className="truncate text-[11px] text-muted-foreground mt-0.5">
          {session.message_count} messages •{" "}
          {formatDistanceToNow(new Date(session.updated_at), {
            addSuffix: true,
          })}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "h-7 w-7 shrink-0 transition-all hover:bg-destructive/10 hover:text-destructive",
          isActive
            ? "opacity-100 text-primary/60"
            : "opacity-0 group-hover:opacity-100 text-muted-foreground/50"
        )}
        onClick={onDelete}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
